/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {RefObject, useCallback, useRef} from 'react';
import {QueryClient} from 'react-query';
import {warn} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import {IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import {JobProgressUpdate} from '@ion/services/src/websocket/types';
import {JobCacheItem, mergeIntoJob, reconcileWithReplay} from './JobCacheMerge';
import {toNumericIds} from './JobSearchEngineUtils';
import useFetchWindowBuffer from './useFetchWindowBuffer';
import useJobSubscriptions from './useJobSubscriptions';
import useSilentReconcile from './useSilentReconcile';

export type JobBlock = GenericObject & {items?: JobCacheItem[]};

export interface JobGridLiveUpdatesProps {
  queryClient: QueryClient,
  ionGridRef: RefObject<IonGridAPI>,
  onRefresh: Function,
  membershipDependsOnStatus?: () => boolean
}

export interface JobGridLiveUpdatesResult {
  observe: (ids: (number | string)[]) => void,
  loadBlock: (
    key: string, load: () => Promise<JobBlock | undefined>
  ) => Promise<JobBlock | undefined>,
  dropJobs: (jobIds: number[]) => void,
  reconciling: boolean
}

/**
 * Applies `jobProgress` changes to the jobs grid. Every accepted change is
 * written twice - into the block's react-query entry, which is what a remount
 * inside the stale window renders from, and onto the grid's row node, which is
 * what repaints now - and `loadBlock` reconciles a response against whatever
 * those writes had merged in before *and during* its own round trip.
 *
 * Subscriptions follow the *cache*, not the page on screen: `observe` declares
 * the union of every page react-query still holds, and a change is routed to
 * whichever of those pages holds that job. So a job finishing on page 1 while
 * the user is on page 2 is already correct when they return, and the second
 * write - the repaint - is skipped for a page which is not the one displayed.
 *
 * A change to `progressStatus` also triggers a reconcile, since it is the one
 * field the grid's own membership and the facet drawer's counts are both
 * server answers about - neither can be recomputed here, so both are
 * refetched, silently and in place. The same transition marks the *other*
 * cached pages stale, but only where the query's membership depends on the
 * status, since patching an entry can never fix which rows a page holds.
 *
 * No block load or repaint re-renders the caller: the grid handle is read at
 * call time and the block key is a ref. `reconciling` is the one exception,
 * and it exists for the surfaces which would otherwise paint a loading state
 * over a refetch nobody asked for.
 *
 * ```typescript
 *
 *   import useJobGridLiveUpdates from './useJobGridLiveUpdates';
 *
 *   const {observe, loadBlock, reconciling} = useJobGridLiveUpdates({
 *     queryClient, ionGridRef, onRefresh, membershipDependsOnStatus
 *   });
 *
 *   // ... inside the grid's datasource callback ...
 *   const response = await loadBlock(
 *     createQuerykey(params), () => loadItems({...params, callback})
 *   );
 *
 *   if (response) {
 *     observe(response.items
 *       .filter(item => !isTerminalJobStatus(item.progressStatus))
 *       .map(item => item.jobId));
 *   }
 *
 * ```
 *
 * @param   queryClient The client holding the block entries being patched.
 * @param   ionGridRef Ref to the grid's imperative handle, `null` while a job
 *          tab has the grid unmounted.
 * @param   onRefresh Drops every job query and reloads, used on reconnect.
 * @param   membershipDependsOnStatus Read at delta time, reports whether a
 *          `progressStatus` move can change which page a job belongs on.
 *          Absent means it never can, and no sibling page is marked stale.
 * @returns `observe` to declare the loaded page's job ids, `loadBlock` to run
 *          a block request through the reconciliation seam, `dropJobs` to take
 *          rows out of the displayed block in place, and `reconciling` while a
 *          refetch nobody asked for is in flight.
 */
export const useJobGridLiveUpdates = ({
  queryClient,
  ionGridRef,
  onRefresh,
  membershipDependsOnStatus
}: JobGridLiveUpdatesProps): JobGridLiveUpdatesResult => {
  const blockKey = useRef<string | null>(null);

  const keyToIds = useRef<Map<string, number[]>>(new Map());
  const jobToKey = useRef<Map<number, string>>(new Map());

  const {loadInWindow, record, changesFor} = useFetchWindowBuffer();

  const runReconcile = useCallback((): boolean => {
    const key = blockKey.current;

    if (key) {
      queryClient.invalidateQueries(key, {exact: true});
    }
    const started = !!ionGridRef.current?.refreshInfiniteCache;

    if (started) {
      ionGridRef.current.refreshInfiniteCache();
    }
    return started;
  }, [queryClient, ionGridRef]);

  const {reconcile, settle, reconciling} = useSilentReconcile({run: runReconcile});

  const pendingDrops = useRef<number[]>([]);

  /**
   * Takes rows out of the displayed block without a datasource rebuild.
   *
   * The Watching tab's filter is the watch list, so an unwatch changes the
   * query and the loud rebuild which would follow purges every block, resets to
   * page 1 and paints a spinner over rows which never left. This stages the
   * splice and starts one silent round instead; `loadBlock` carries the block
   * forward under that round's own key, so the row leaves on this tick.
   *
   * Nothing is staged unless every id is on screen and the grid is mounted: a
   * total decremented for a row this page never held addresses a block ag-grid
   * then asks for and nothing seeded, and a splice staged with no round to
   * consume it lands on whichever block loads next instead. The reconcile still
   * runs in that case - the rows simply arrive from the service.
   *
   * @param   jobIds The ids the unwatch removed.
   */
  const dropJobs = useCallback((jobIds: number[]): void => {
    const key    = blockKey.current;
    const block  = key ? queryClient.getQueryData<JobBlock>(key) : undefined;
    const items  = block?.items ?? [];
    const onPage = !!items.length &&
      jobIds.every(id => items.some(item => Number(item.jobId) === id));

    pendingDrops.current = onPage && !!ionGridRef.current?.refreshInfiniteCache ? jobIds : [];
    reconcile();
  }, [queryClient, ionGridRef, reconcile]);

  /**
   * Carries the displayed block forward under this round's own key, minus the
   * ids the unwatch removed.
   *
   * Written fresh, which is the whole point: the `fetchQuery` underneath
   * `loadItems` resolves from an entry inside its `staleTime` rather than from
   * the network, so the row leaves now instead of a round trip from now.
   *
   * `totalCount` is decremented because it drives the pagination bar and
   * ag-grid's own row count; `aggregations` are carried untouched. Both are
   * server answers this hook is only guessing at, which is why `loadBlock`
   * fires the authoritative round the moment this one has painted.
   *
   * @param   key The key this round's response will be stored under.
   * @returns `true` when a block was carried forward.
   */
  const carryDrops = useCallback((key: string): boolean => {
    const dropped = pendingDrops.current;
    const from    = blockKey.current;

    pendingDrops.current = [];
    const block = dropped.length && from && from !== key
      ? queryClient.getQueryData<JobBlock>(from)
      : undefined;

    if (block) {
      const items = (block.items ?? []).filter(item => !dropped.includes(Number(item.jobId)));

      queryClient.setQueryData(key, {
        ...block,
        items,
        totalCount : Math.max(0, (block.totalCount ?? items.length) - dropped.length)
      });
    }
    return !!block;
  }, [queryClient]);

  const prune = useCallback((): void => {
    keyToIds.current.forEach((ids, key) => {
      if (!queryClient.getQueryData(key)) {
        keyToIds.current.delete(key);
        ids.forEach(id => {
          if (jobToKey.current.get(id) === key) {
            jobToKey.current.delete(id);
          }
        });
      }
    });
  }, [queryClient]);

  const onDelta = useCallback((delta: JobProgressUpdate, jobId: number): void => {
    record(jobId, delta);

    const key   = jobToKey.current.get(jobId);
    const block = key ? queryClient.getQueryData<JobBlock>(key) : undefined;
    const items = block?.items ? [...block.items] : [];
    const index = items.findIndex(item => item.jobId === jobId);
    const previous = index === -1 ? undefined : items[index];
    const {accepted, job, unplaced} = mergeIntoJob(previous, delta);

    if (unplaced?.length) {
      warn(`[ADM] jobProgress change for job ${jobId} addressed task index(es) ` +
        `[${unplaced.join(',')}] which the cached job does not hold, the task ` +
        'scope of the change was dropped');
    }

    if (accepted && job && key) {
      items[index] = job;
      queryClient.setQueryData(key, {...block, items});
      if (key === blockKey.current) {
        ionGridRef.current?.updateRowNodes?.([job], false);
      }

      if (job.progressStatus !== previous?.progressStatus) {
        reconcile();

        if (membershipDependsOnStatus?.()) {
          keyToIds.current.forEach((_, other) => {
            if (other !== blockKey.current) {
              queryClient.invalidateQueries(other, {exact: true});
            }
          });
        }
      }
    }
  }, [queryClient, ionGridRef, record, reconcile, membershipDependsOnStatus]);

  const onResync = useCallback((): void => {
    onRefresh();
  }, [onRefresh]);

  const {observe: observeIds} = useJobSubscriptions({onDelta, onResync});

  /**
   * Records the ids the freshly loaded page declared against the key it was
   * stored under, then declares the union of every page still cached.
   *
   * The union is built here rather than in `useJobSubscriptions`, whose
   * documented contract - `observe(ids)` replaces this consumer's id set - is
   * what makes re-declaring a page the unsubscribe path for the one before it.
   * Nothing below this hook needs a concept of a page for that to hold.
   *
   * @param   ids The job ids of the page just loaded.
   */
  const observe = useCallback((ids: (number | string)[]): void => {
    const key   = blockKey.current;
    const union = new Set<number>();

    if (key) {
      const declared = toNumericIds(ids);

      keyToIds.current.set(key, declared);
      declared.forEach(id => jobToKey.current.set(id, key));
    }
    keyToIds.current.forEach(declared => declared.forEach(id => union.add(id)));
    observeIds(Array.from(union));
  }, [observeIds]);

  /**
   * Reconciles a freshly loaded block against every change the entry had merged
   * in before the request went out and every change which arrived while it was
   * in flight, then repaints only the rows the reconciliation moved.
   *
   * The repaint is not optional: the grid's callback is handed the *unmerged*
   * response synchronously inside `loadItems`, before it resolves, so the rows
   * are already painted from the response by the time this runs. Without it
   * the entry holds the right value and the screen shows the reverted one -
   * indefinitely, for a job which went terminal inside the window.
   *
   * @param   key The query key the response was stored under.
   * @param   cached The entry read *before* awaiting the response.
   * @param   incoming The loaded response.
   */
  const mergeBlockSnapshot = useCallback((
    key: string, cached: JobBlock | undefined, incoming: JobBlock
  ): void => {
    const changed: JobCacheItem[] = [];
    const items                   = (incoming?.items ?? []).map(item => {
      const previous = cached?.items?.find(entry => entry.jobId === item.jobId);
      const {job, moved} = reconcileWithReplay(previous, item, changesFor(Number(item.jobId)));

      if (moved) {
        changed.push(job);
      }
      return job;
    });

    if (cached || changed.length) {
      queryClient.setQueryData(key, {...incoming, items});
    }
    if (changed.length) {
      ionGridRef.current?.updateRowNodes?.(changed, false);
    }
  }, [queryClient, ionGridRef, changesFor]);

  /**
   * Runs one block request through the reconciliation seam: reads the entry
   * before awaiting, records the changes arriving for the duration of the round
   * trip, then reconciles the response against both.
   *
   * The window's own obligations - read before awaiting, buffer what arrives,
   * drop it on every outcome including a failed load - belong to
   * `useFetchWindowBuffer`. What is left here is what the *grid* owes: the key
   * this request will be stored under, named on every outcome because a failed
   * load still changes which block is displayed, and the reconciliation itself.
   *
   * @param   key The query key this request will be stored under.
   * @param   load Runs the request - `loadItems` with the grid's callback.
   * @returns Whatever `load` resolved, untouched, for the grid's datasource.
   */
  const loadBlock = useCallback((
    key: string, load: () => Promise<JobBlock | undefined>
  ): Promise<JobBlock | undefined> => {
    const carried = carryDrops(key);

    return loadInWindow<JobBlock>({
      read   : () => queryClient.getQueryData<JobBlock>(key),
      load,
      settle : (cached, response) => {
        blockKey.current = key;
        prune();
        settle();
        if (response) {
          mergeBlockSnapshot(key, cached, response);
        }
        if (carried) {
          reconcile();
        }
      }
    });
  }, [loadInWindow, queryClient, mergeBlockSnapshot, prune, settle, carryDrops, reconcile]);

  return {observe, loadBlock, dropJobs, reconciling};
};

export default useJobGridLiveUpdates;
